/**
 * Calculate escalated rent amount based on rent start date and escalation percentage
 * @param {number} baseRentAmount - The original rent amount
 * @param {number} escalationPercentage - The escalation percentage (e.g., 5 for 5%)
 * @param {Date|string} rentStartDate - The date when rent started
 * @param {Date|string} invoiceDate - The date for which rent is being calculated
 * @returns {number} The escalated rent amount
 */
export function calculateEscalatedRent(baseRentAmount, escalationPercentage, rentStartDate, invoiceDate) {
  // If no escalation percentage or rent start date, return base amount
  if (!escalationPercentage || !rentStartDate || !baseRentAmount) {
    return baseRentAmount || 0;
  }

  // Convert dates to Date objects if they're strings
  const startDate = new Date(rentStartDate);
  const targetDate = new Date(invoiceDate);
  
  // If target date is before start date, return base amount
  if (targetDate < startDate) {
    return baseRentAmount;
  }

  // Calculate the number of full years between start date and target date
  // We need to check if the anniversary date has actually passed
  const startMonth = startDate.getMonth();
  const startDay = startDate.getDate();
  const targetMonth = targetDate.getMonth();
  const targetDay = targetDate.getDate();
  const targetYear = targetDate.getFullYear();
  const startYear = startDate.getFullYear();
  
  // Calculate years elapsed, but only if the anniversary date has passed
  let yearsElapsed = targetYear - startYear;
  
  // If we haven't reached the anniversary date yet, reduce by 1 year
  if (targetMonth < startMonth || (targetMonth === startMonth && targetDay < startDay)) {
    yearsElapsed--;
  }

  // If less than 1 year has passed, return base amount
  if (yearsElapsed < 1) {
    return baseRentAmount;
  }

  // Calculate escalated amount: base * (1 + escalation%)^years
  const escalationMultiplier = Math.pow(1 + (escalationPercentage / 100), yearsElapsed);
  const escalatedAmount = baseRentAmount * escalationMultiplier;

  // Round to 2 decimal places for currency
  return Math.round(escalatedAmount * 100) / 100;
}

/**
 * Get escalation information for display purposes
 * @param {number} baseRentAmount - The original rent amount
 * @param {number} escalationPercentage - The escalation percentage
 * @param {Date|string} rentStartDate - The date when rent started
 * @param {Date|string} invoiceDate - The date for which rent is being calculated
 * @returns {object} Object containing escalation details
 */
export function getEscalationInfo(baseRentAmount, escalationPercentage, rentStartDate, invoiceDate) {
  const escalatedAmount = calculateEscalatedRent(baseRentAmount, escalationPercentage, rentStartDate, invoiceDate);
  const escalationAmount = escalatedAmount - baseRentAmount;
  
  // Calculate years elapsed using the same logic as calculateEscalatedRent
  const startDate = new Date(rentStartDate);
  const targetDate = new Date(invoiceDate);
  
  const startMonth = startDate.getMonth();
  const startDay = startDate.getDate();
  const targetMonth = targetDate.getMonth();
  const targetDay = targetDate.getDate();
  const targetYear = targetDate.getFullYear();
  const startYear = startDate.getFullYear();
  
  let yearsElapsed = targetYear - startYear;
  
  // If we haven't reached the anniversary date yet, reduce by 1 year
  if (targetMonth < startMonth || (targetMonth === startMonth && targetDay < startDay)) {
    yearsElapsed--;
  }
  
  yearsElapsed = Math.max(0, yearsElapsed);

  return {
    baseAmount: baseRentAmount,
    escalatedAmount: escalatedAmount,
    escalationAmount: escalationAmount,
    escalationPercentage: escalationPercentage,
    yearsElapsed: yearsElapsed,
    hasEscalation: yearsElapsed > 0 && escalationPercentage > 0
  };
}
